// src/lib/audio/permissions.ts

export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported'

type EnsureMicResult = {
  ok: boolean
  state: MicPermissionState
  error?: string
}

/**
 * Best-effort read of current microphone permission without prompting.
 * Returns:
 *  - 'granted' | 'denied' | 'prompt' when Permissions API is available
 *  - 'unsupported' when we can't query
 */
export async function queryMicPermission(): Promise<MicPermissionState> {
  try {
    const navAny = navigator as any
    const perms = navAny.permissions
    if (!perms?.query) return 'unsupported'

    const status = await perms.query({ name: 'microphone' as any })

    if (status.state === 'granted') return 'granted'
    if (status.state === 'denied') return 'denied'
    return 'prompt'
  } catch {
    // Permissions API unsupported or blocked
    return 'unsupported'
  }
}

/**
 * Utility to stop all tracks on a MediaStream.
 * VERY IMPORTANT for Safari so we don't accidentally leave a "hot mic".
 */
function stopAllTracks(stream?: MediaStream | null) {
  if (!stream) return
  try {
    stream.getTracks?.().forEach(track => {
      try {
        track.stop()
      } catch {
        // ignore
      }
    })
  } catch {
    // ignore
  }
}

/**
 * Ensure we have mic permission and **do not** leave any stray MediaStream running.
 *
 * Behavior:
 *  - If permission is already 'granted' → ok = true (no getUserMedia call).
 *  - If 'denied' → ok = false.
 *  - If 'prompt' or 'unsupported':
 *      * We call getUserMedia({ audio: true }) under the calling gesture.
 *      * On success, we immediately stop all tracks and return ok = true.
 *      * On failure, ok = false.
 *
 * This pattern makes sure that any "probe" stream is ephemeral and
 * only Zello's own stream is long-lived, which your watchdog can control.
 */
export async function ensureMicPermission(): Promise<EnsureMicResult> {
  const state = await queryMicPermission()

  if (state === 'granted') {
    return { ok: true, state: 'granted' }
  }

  if (state === 'denied') {
    return { ok: false, state: 'denied', error: 'Microphone permission denied' }
  }

  // 'prompt' or 'unsupported' → try getUserMedia once under user gesture
  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      return {
        ok: false,
        state: 'unsupported',
        error: 'MediaDevices.getUserMedia is not available in this browser',
      }
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true },
    })

    // 🔴 CRITICAL: stop all tracks immediately so we don't leave a "hot mic".
    stopAllTracks(stream)

    // If this succeeded, browser has effectively granted mic access for this origin.
    return { ok: true, state: 'granted' }
  } catch (e: any) {
    const msg = e?.message ?? String(e)
    return {
      ok: false,
      state: state === 'prompt' ? 'prompt' : 'unsupported',
      error: msg,
    }
  }
}
